import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as files from 'store/slices/files';
import {combine, dropTargetForElements, isDndData} from '../utils/dragdrop';

import type {CleanupFn} from '@atlaskit/pragmatic-drag-and-drop/types';
import {useGetFileDetailsCached} from 'interface/common/hooks/useGetFileDetailsCached';
import type {View} from 'react-native';
import * as aichat from 'store/slices/aichat';
import {useGetNamespaceContext} from '../../hooks/useGetNamespaceContext';

import {useToast} from 'react-native-toast-notifications';
import type {FilesItem} from 'store/slices/files/types';

interface UseDragDropChatTargetProps {
  ref: React.RefObject<View>;
}

export function useDragDropChatTarget(props: UseDragDropChatTargetProps) {
  const {ref} = props;
  const [isDropping, setIsDropping] = useState(false);
  const selection = useSelector(files.selectors.getSelection);
  const {namespace, instanceId} = useGetNamespaceContext();
  const {items: selectedFiles} = useGetFileDetailsCached(
    selection,
    instanceId,
    namespace,
  );

  const dispatch = useDispatch();
  const toast = useToast();

  const isFile = (file: FilesItem) =>
    file.type === 'file' || file.type === 'note';

  useEffect(() => {
    if (!ref?.current) return;
    const element = ref.current as unknown as HTMLElement;
    return combine(
      ...([
        dropTargetForElements({
          element,
          canDrop: ({source}) => isDndData(source.data),
          onDragEnter: () => setIsDropping(true),
          onDragLeave: () => setIsDropping(false),
          onDrop: async e => {
            setIsDropping(false);

            if (isDndData(e.source.data)) {
              const files =
                selection.length > 1 ? selectedFiles : [e.source.data.item];
              const attachableFiles = files.filter(f => f.ai?.attach);

              if (attachableFiles.length === 0) {
                if (files.length === 1) {
                  toast.show('This file is not attachable');
                } else {
                  toast.show('These files are not attachable');
                }
                return;
              }
              if (attachableFiles.length !== files.length) {
                toast.show('Some files are not attachable to AI Chat');
              }

              if (attachableFiles.every(isFile)) {
                dispatch(
                  aichat.default.actions.addAttachFiles({
                    files: attachableFiles,
                  }),
                );
              } else if (attachableFiles.every(f => f.type === 'folder')) {
                dispatch(
                  aichat.default.actions.addScopeFiles({
                    files: attachableFiles,
                  }),
                );
              } else {
                const onlyFiles = attachableFiles.filter(isFile);
                dispatch(
                  aichat.default.actions.addAttachFiles({files: onlyFiles}),
                );
              }
            }
          },
        }),
      ].filter(Boolean) as CleanupFn[]),
    );
  }, [selection, ref?.current]);

  return {isDropping};
}
